import { coerceNumberProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { Status } from '@df/core/status';
import { AutoSizeInputDirective } from '@df/ui/form/auto-size-input.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Product } from '../../catalog/product/product.decorator';
@Component({
    selector: 'basket-item-qty',
    templateUrl: './basket-item-qty.html',
    styleUrls: ['basket-item-qty.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TranslationModule, FormsModule, AutoSizeInputDirective]
})
export class BasketItemQtyComponent implements OnChanges, OnInit, OnDestroy {
    @Input()
    set product(product: Product) {
        this.min = (<IProductDataV2>product.data)?.min_sale_qty || 1;
        this.step = (<IProductDataV2>product.data)?.qty_increments || 1;
        this.model = this.model || 1;
    }
    /**
     * Min qty
     * @type {number}
     */
    min?: number = 1;

    step?: number = 1;

    /**
     * Max qty
     * @type {number}
     */
    @Input()
    max?: number;

    /**
     * Status setter
     * Status is required
     */
    protected _status?: Status;

    @Input()
    set status(status: Status) {
        this._status = status;

        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }

        if (status) {
            this.subscription = status.subscribe(() => this.onStatusChange());
        }
    }
    get status(): Status | undefined {
        return this._status;
    }

    /**
     * Is disabled
     * @type {boolean}
     */
    @Input()
    isDisabled?: boolean;

    @Input()
    debounce = 100;

    @Input()
    custom: any;

    /**
     * Model values setter
     * @param {number | string | null} value
     */
    protected _model?: number;

    @Input()
    set model(value: number | string | null) {
        if (this.status?.busy) {
            return;
        }

        if (typeof value === 'number') {
            if (typeof this.min === 'number' && value < this.min) {
                value = this.min;
            }

            if (this.max !== undefined && value > this.max) {
                value = this.max;
            }

            const divRemainder = value % this.step;
            if (divRemainder && value > this.min) {
                value = Math.max(value - divRemainder, this.min);
            }

            if (value !== this.model) {
                this._model = value;
                this.emitChange();
            }
        } else if (this.inputElement?.nativeElement) {
            this._model = undefined;
        }
        this.setInputValue();
    }

    protected setInputValue() {
        if (this.inputElement) {
            this.inputElement.nativeElement.value = this.model === undefined ? '' : this.model.toString();
        }
    }

    get model(): number | string | null {
        return this._model;
    }

    @Output()
    modelChange: EventEmitter<number | string> = new EventEmitter<number | string>();

    /**
     * On change function to call
     *
     * Typical usage will be either
     * (qtyChange)="setQty($event)"
     *
     * @type {function}
     */
    @Output()
    qtyChange: EventEmitter<number | string> = new EventEmitter<number | string>();

    @ViewChild('input')
    inputElement?: ElementRef<HTMLInputElement>;

    /**
     * Status subscription
     * @type {[type]}
     */
    protected subscription?: Subscription;
    protected subscriptions = new Subscription();

    /**
     * Class constructor
     * @param {ElementRef} protected ngEl
     * @param {Renderer}   protected renderer
     */
    public constructor(
        private ref: ChangeDetectorRef,
        readonly meta: Meta
    ) {}

    ngOnInit(): void {
        const debounce = coerceNumberProperty(this.debounce, 100);

        this.subscriptions.add(
            this.modelChange
                .pipe(
                    debounceTime(debounce), // wait ms after the last event before emitting last event
                    distinctUntilChanged() // only emit if value is different from previous value
                )
                .subscribe(model => this.qtyChange.emit(model))
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['min']) {
            if (typeof this.min !== 'number') {
                this.min = 1;
            }

            if (!this.model || Number(this.model) < this.min) {
                this.model = this.min;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
    }

    /**
     * Increases qty
     * @param  {int} qty default 1
     */
    more(): void {
        if (this.canMore()) {
            (this.model as number) = ((this.model as number) || 0) + (this.step || 0);
            this.detectChanges();
        }
    }

    /**
     * Decreases qty
     * @param {int} qty default 1
     */
    less(): void {
        if (this.canLess()) {
            (this.model as number) = ((this.model as number) || 0) - (this.step || 0);
            this.detectChanges();
        }
    }

    protected getValueForIncrementCheck() {
        return this.model;
    }

    canLess(): boolean {
        const value = this.getValueForIncrementCheck();
        if (this.isDisabled || typeof value !== 'number') {
            return false;
        }

        if (typeof this.min === 'number' && this.min >= value) {
            return false;
        }

        return true;
    }

    canMore(): boolean {
        const value = this.getValueForIncrementCheck();

        if (this.isDisabled || typeof value !== 'number') {
            return false;
        }

        if (typeof this.max === 'number' && this.max <= value) {
            return false;
        }

        return true;
    }

    validate() {
        if (this.model === undefined) {
            this.model = this.min !== undefined ? this.min : 0;
        }
    }

    /**
     * If component onChange is set then notify
     */
    emitChange(): void {
        if (this.model !== undefined) {
            this.modelChange.next(this.model);
        }
    }

    onStatusChange() {
        this.validate();

        this.detectChanges();
    }

    detectChanges(): void {
        if (!(<ViewRef>this.ref).destroyed) {
            this.ref.detectChanges();
        }
    }
}
